/* global Word */

/**
 * DocPilot AI — Document Structure Extractor (v2)
 * 
 * FIXES:
 * - Added detectedHeadingLevel to each paragraph so AI knows what level we computed
 * - Added consecutiveEmptyBefore count for empty paragraph detection
 * - Increased text truncation to 300 chars
 * - Added exportLastJSON() for debugging — saves JSON to console
 */

export interface ExtractedParagraph {
  index: number;
  text: string;
  style: string;
  font: { name: string; size: number; bold: boolean; italic: boolean };
  isInTable: boolean;
  isInHeader: boolean;
  isListItem: boolean;
  listPrefix: string;
  leftIndent: number;
  spaceBefore: number;
  spaceAfter: number;
  alignment: string;
  isEmpty: boolean;
  hasInlineImage: boolean;
  detectedHeadingLevel: number;    // 0 = not a heading, 1-9 = heading level
  consecutiveEmptyBefore: number;  // how many empty paras before this one
}

export interface ExtractedTable {
  index: number;
  paragraphIndexBefore: number;
  rowCount: number;
  columnCount: number;
  hasHeaderRow: boolean;
  captionText: string;
}

export interface DocumentStructure {
  metadata: {
    extractedAt: string;
    totalParagraphs: number;
    totalTables: number;
    sectionCount: number;
  };
  paragraphs: ExtractedParagraph[];
  tables: ExtractedTable[];
  tocEntries: string[];
  headingHierarchy: { text: string; level: number; index: number }[];
}

// ── Bullet detection ──
const BULLET_CHARS = new Set([
  '\u2022', '\u25CB', '\u25AA', '\u25AB', '\u25A0', '\u25A1',
  '\u25C6', '\u25C7', '\u27A2', '\u27A3', '\u2B9A',
  '\u00B7', '\u2219', '\uF0B7', '\uF0A7', '\uF076', '\uF0D8',
  '\uF0FC', '\uF0E8', '\uF0A8', '\uF071', '\uF06C', '\uF06E',
  '\u25CF', '\u25B6', '\u25C0', '\u25BA', '\u25C4', '\u2666',
  '\u2605', '\u2713', '\u2714', '\u2717', '\u2718', '\u27A4',
  '\u2023', '\u2043', '\u204D'
]);

function detectListPrefix(text: string): { isListItem: boolean; prefix: string } {
  if (!text || text.trim().length === 0) return { isListItem: false, prefix: "" };
  const trimmed = text.trim();
  const firstChar = trimmed.charAt(0);
  if (BULLET_CHARS.has(firstChar)) return { isListItem: true, prefix: firstChar };
  const numMatch = trimmed.match(/^(\d{1,3}[.)]\s)/);
  if (numMatch) return { isListItem: true, prefix: numMatch[1].trim() };
  const alphaMatch = trimmed.match(/^([a-zA-Z][.)]\s)/);
  if (alphaMatch) return { isListItem: true, prefix: alphaMatch[1].trim() };
  return { isListItem: false, prefix: "" };
}

// ── Heading detection ──
// Matches: "1.0 ", "2.3.1 ", "14.2.1.3 ", etc.
const HEADING_PATTERN = /^(\d{1,3}(?:\.\d{1,3})*\.?\s)/;

function detectHeadingLevel(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;

  const match = trimmed.match(HEADING_PATTERN);
  if (!match) return 0;

  const numPart = match[1].trim().replace(/\.$/, "");
  const segments = numPart.split(".");

  // Filter out empty segments from trailing dots
  const validSegments = segments.filter(s => s.length > 0);
  if (validSegments.length === 0) return 0;

  // "X.0" pattern is always Heading 1 (top-level)
  if (validSegments.length === 2 && validSegments[1] === "0") return 1;

  return validSegments.length;
}

// ── Store last extraction for debugging ──
let _lastExtractedJSON: DocumentStructure | null = null;

/** Returns the last extracted document JSON (for debugging) */
export function getLastExtractedJSON(): DocumentStructure | null {
  return _lastExtractedJSON;
}

/**
 * Main extraction function
 */
export async function extractDocumentStructure(): Promise<DocumentStructure> {
  console.log("[DocPilot Extractor] Starting document extraction...");
  const startTime = Date.now();

  const paragraphs: ExtractedParagraph[] = [];
  const tables: ExtractedTable[] = [];
  const tocEntries: string[] = [];
  const headingHierarchy: { text: string; level: number; index: number }[] = [];
  let sectionCount = 0;

  await Word.run(async (context) => {
    const body = context.document.body;
    const bodyParas = body.paragraphs;
    bodyParas.load("items");
    await context.sync();

    const total = bodyParas.items.length;
    console.log(`[DocPilot Extractor] Found ${total} paragraphs`);

    // Track consecutive empty paragraphs
    let consecutiveEmpty = 0;

    const BATCH_SIZE = 100;
    for (let batchStart = 0; batchStart < total; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, total);

      for (let i = batchStart; i < batchEnd; i++) {
        const p = bodyParas.items[i];
        p.load("text,style,font,leftIndent,spaceBefore,spaceAfter,alignment,isListItem,inlinePictures");
        p.font.load("name,size,bold,italic");
      }
      await context.sync();

      for (let i = batchStart; i < batchEnd; i++) {
        const p = bodyParas.items[i];
        const text = (p.text || "").replace(/\r/g, "");
        const listInfo = detectListPrefix(text);
        const headingLevel = detectHeadingLevel(text);
        const isEmpty = text.trim().length === 0;

        // Track consecutive empties
        if (isEmpty) {
          consecutiveEmpty++;
        } else {
          consecutiveEmpty = 0;
        }

        const extracted: ExtractedParagraph = {
          index: i,
          text: text.substring(0, 300),  // Increased from 200 for better AI context
          style: p.style || "",
          font: {
            name: p.font.name || "",
            size: p.font.size || 0,
            bold: p.font.bold || false,
            italic: p.font.italic || false
          },
          isInTable: false,
          isInHeader: false,
          isListItem: p.isListItem || listInfo.isListItem,
          listPrefix: listInfo.prefix,
          leftIndent: p.leftIndent || 0,
          spaceBefore: p.spaceBefore || 0,
          spaceAfter: p.spaceAfter || 0,
          alignment: p.alignment || "Left",
          isEmpty: isEmpty,
          hasInlineImage: (p.inlinePictures?.items?.length || 0) > 0,
          detectedHeadingLevel: headingLevel,
          consecutiveEmptyBefore: isEmpty ? consecutiveEmpty : 0
        };

        paragraphs.push(extracted);

        if (headingLevel > 0 && headingLevel <= 9) {
          headingHierarchy.push({ text: text.substring(0, 100), level: headingLevel, index: i });
        }

        if (extracted.style.toLowerCase().includes("toc")) {
          tocEntries.push(text.substring(0, 150));
        }
      }

      console.log(`[DocPilot Extractor] Batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: paras ${batchStart}–${batchEnd - 1}`);
    }

    // ── Load tables ──
    const bodyTables = body.tables;
    bodyTables.load("items");
    await context.sync();

    for (let t = 0; t < bodyTables.items.length; t++) {
      const table = bodyTables.items[t];
      table.load("rowCount,values,headerRowCount");
      try {
        const tableRange = table.getRange("Whole");
        const beforeRange = tableRange.getRange("Before");
        const paraBefore = beforeRange.paragraphs;
        paraBefore.load("items");
        await context.sync();

        let captionText = "";
        let paraIndexBefore = -1;

        if (paraBefore.items.length > 0) {
          const lastParaBefore = paraBefore.items[paraBefore.items.length - 1];
          lastParaBefore.load("text");
          await context.sync();
          captionText = (lastParaBefore.text || "").substring(0, 100);

          for (let pi = 0; pi < paragraphs.length; pi++) {
            if (paragraphs[pi].text === captionText.substring(0, 300)) {
              paraIndexBefore = pi;
              break;
            }
          }
        }

        tables.push({
          index: t,
          paragraphIndexBefore: paraIndexBefore,
          rowCount: table.rowCount || 0,
          columnCount: (table.values && table.values[0]) ? table.values[0].length : 0,
          hasHeaderRow: (table.headerRowCount || 0) > 0,
          captionText: captionText
        });
      } catch (tableErr) {
        console.warn(`[DocPilot Extractor] Table ${t} error:`, tableErr.message);
        tables.push({ index: t, paragraphIndexBefore: -1, rowCount: 0, columnCount: 0, hasHeaderRow: false, captionText: "" });
      }
    }

    // ── Count sections ──
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    sectionCount = sections.items.length;
  });

  const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
  console.log(`[DocPilot Extractor] ✓ Done in ${elapsed}s — ${paragraphs.length} paras, ${tables.length} tables`);

  const result: DocumentStructure = {
    metadata: {
      extractedAt: new Date().toISOString(),
      totalParagraphs: paragraphs.length,
      totalTables: tables.length,
      sectionCount: sectionCount
    },
    paragraphs,
    tables,
    tocEntries,
    headingHierarchy
  };

  // Cache for debugging
  _lastExtractedJSON = result;

  return result;
}